/**
 * Page rebind — Tier 4 album swap (Animation Spec §6.2).
 *
 * Choreography (1100ms total, reduced-motion fallback: 200ms crossfade):
 *   t=0     pickedId starts the cascade. The picker overlay closes (its own 200ms fade).
 *   t=0     A subtle backdrop flash fades in (rebind.flash 160ms, accent radial gradient).
 *   t=160   Backdrop starts fading out (rebind.overlay-exit 300ms).
 *   t=200   React state swaps to the new album. AnimatePresence handles unmount.
 *   t=200   Cover lands first (cover-in 600ms, key={themeKey} replays the keyframe).
 *   t=300   Hero gap-eyebrow + word-stagger replay (each cascadeStep apart).
 *   t=380   Topbar pulse / breadcrumb refresh.
 *   t=460   Below-fold sections crossfade.
 *   t=1100  Done. is-swapping class lifts; the page is fully rebound.
 *
 * The orchestrator drives:
 *   - an `is-swapping` class on <html>
 *   - a `--rebind-step` CSS var (0 → 1, normalized progress) for any
 *     consumer that wants to hook into the cascade
 *
 * Children that just need to replay their own animation use `key={themeKey}`
 * (which we already do for Hero, DeepDive, Songs, Footer).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from './reducedMotion';

const REBIND_TOTAL_MS = 1100;
const REBIND_FLASH_MS = 160;
const REBIND_OVERLAY_EXIT_MS = 300;
const REDUCED_MOTION_MS = 200;

interface PageRebindState {
  /** Currently displayed album id (drives subtree key). */
  themeKey: string;
  /** Whether a swap is currently in flight. */
  isSwapping: boolean;
  /** Trigger an album swap. Caller passes the picked id. */
  rebind: (nextId: string) => void;
}

interface UsePageRebindArgs {
  /** Source album id (from useCurrentAlbum). */
  currentId: string;
  /** Setter that actually changes the URL + active album (from useCurrentAlbum). */
  setCurrent: (id: string) => void;
}

export function usePageRebind({ currentId, setCurrent }: UsePageRebindArgs): PageRebindState {
  const [themeKey, setThemeKey] = useState(currentId);
  const [isSwapping, setIsSwapping] = useState(false);

  const swapTimer = useRef<number | null>(null);
  const finishTimer = useRef<number | null>(null);
  const root = useRef<HTMLElement | null>(null);

  // Prefer the <html> element for the global `is-swapping` flag so any CSS
  // anywhere on the page can hook into it.
  useEffect(() => {
    root.current = document.documentElement;
  }, []);

  // Keep themeKey in sync if the URL changes externally (back/forward).
  useEffect(() => {
    if (themeKey !== currentId && !isSwapping) {
      setThemeKey(currentId);
    }
  }, [currentId, themeKey, isSwapping]);

  const clearTimers = () => {
    if (swapTimer.current) {
      window.clearTimeout(swapTimer.current);
      swapTimer.current = null;
    }
    if (finishTimer.current) {
      window.clearTimeout(finishTimer.current);
      finishTimer.current = null;
    }
  };

  useEffect(() => clearTimers, []);

  const rebind = useCallback(
    (nextId: string) => {
      if (nextId === currentId || isSwapping) return;
      const reduced = prefersReducedMotion();
      const total = reduced ? REDUCED_MOTION_MS : REBIND_TOTAL_MS;
      const swapAt = reduced ? Math.floor(total / 2) : REBIND_FLASH_MS + REBIND_OVERLAY_EXIT_MS / 4;

      clearTimers();

      // Phase 1 — start the swap. Apply class so the page-level flash CSS triggers.
      setIsSwapping(true);
      root.current?.classList.add('is-swapping');
      root.current?.style.setProperty('--rebind-progress', '0');

      // Animate `--rebind-progress` from 0 → 1 across the whole window.
      const startedAt = performance.now();
      let raf = 0;
      const step = (now: number) => {
        const t = Math.min(1, (now - startedAt) / total);
        root.current?.style.setProperty('--rebind-progress', t.toFixed(3));
        if (t < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);

      // Phase 2 — actually swap state once the flash has bloomed.
      swapTimer.current = window.setTimeout(() => {
        setCurrent(nextId);
        setThemeKey(nextId);
      }, swapAt);

      // Phase 3 — finish: lift class + flag.
      finishTimer.current = window.setTimeout(() => {
        cancelAnimationFrame(raf);
        root.current?.classList.remove('is-swapping');
        root.current?.style.removeProperty('--rebind-progress');
        setIsSwapping(false);
      }, total);
    },
    [currentId, isSwapping, setCurrent]
  );

  return { themeKey, isSwapping, rebind };
}
